/**
 * Threshold Coupon Strategy
 * Handles THRESHOLD type coupons with tiered discounts
 * Extends MarketingCouponStrategy as it's a subtype of marketing coupons
 */
import { MarketingCouponStrategy } from './marketing-coupon-strategy';
import { couponTypes, couponSourceTypeMap } from '../helper/constant';
import {
  getDefaultCouponState,
  thresholdCouponUtil,
  getInitialDiscountResult,
  filterNotSupportPlanCodesForThresholdCoupon,
  ThresholdCouponUtil,
} from '../helper/utils';
import { getNextPstCycleDate } from '@/utils/dateformat';

export class ThresholdCouponStrategy extends MarketingCouponStrategy {
  /**
   * Get coupon type identifier
   * @returns {string} MARKETING coupon type (threshold is a subtype)
   */
  getType() {
    return couponTypes.MARKETING;
  }

  /**
   * Get source type identifier
   * @returns {string} THRESHOLD source type
   */
  getSourceType() {
    return couponSourceTypeMap.threshold;
  }

  /**
   * Calculate discount for THRESHOLD coupon
   * @param {Object} couponState - Current coupon state with threshold info
   * @param {Array} selectedPlans - Selected plans array
   * @param {Object} planWithAmountMap - Plan amount mapping
   * @param {Object} context - Additional context (getters for effectSelectedPlansMap)
   * @returns {Object} Calculated discount result with threshold info
   */
  calculateDiscount(couponState, selectedPlans, planWithAmountMap, context) {
    try {
      // Use threshold utility to calculate tier-based discount
      // This is the same logic as generateThresholdCoupon in utils.js
      const thresholdResult = thresholdCouponUtil.calculateDisCount(couponState, context);
      const thresholdDiscountPlans = thresholdResult.thresholdDiscountPlans || {};

      // Get expire dates for different billing cycles
      const expireDates = getNextPstCycleDate();

      // Process each plan with threshold discount using static utility methods
      // These are the same methods used in generateThresholdCoupon
      const processedPlans = Object.entries(thresholdDiscountPlans)
        .map(([planCode, plan]) => ThresholdCouponUtil.processPlan(planCode, plan, expireDates))
        .filter(Boolean);

      // Aggregate the results using static utility method
      const aggregatedResult = ThresholdCouponUtil.aggregate(processedPlans);

      return {
        ...aggregatedResult,
        thresholdResult,
      };
    } catch (error) {
      console.error('Error calculating threshold coupon discount:', error);
      return {
        ...getInitialDiscountResult(),
        thresholdResult: { error: error.message },
      };
    }
  }

  /**
   * Convert threshold coupon result to coupon state
   * Handles both verify coupon API result and campaign coupon result
   * @param {Object} marketingCoupon - Marketing coupon data with threshold info
   * @param {Object} options - Conversion options
   * @param {string} options.couponCode - Override coupon code
   * @param {boolean} options.allowChange - Whether coupon can be changed
   * @returns {Object} Coupon state
   */
  // eslint-disable-next-line no-unused-vars
  convertApiResultToState(marketingCoupon, options = {}) {
    const { couponCode: overrideCouponCode, allowChange = true } = options;
    const {
      couponCode,
      thresholdList = [],
      supportPlanCodeList = [],
      promoAllPlans = false,
      tncMessage,
      // Spread remaining fields for verify coupon API compatibility
      ...restFields
    } = marketingCoupon || {};

    // Filter unsupported plan codes
    const filteredSupportPlanCodeList = filterNotSupportPlanCodesForThresholdCoupon(supportPlanCodeList);

    return {
      ...getDefaultCouponState(),
      ...restFields, // Include any additional fields from verify API
      couponCode: overrideCouponCode || couponCode,
      tncMessage,
      promoAllPlans,
      thresholdList,
      supportPlanCodeList: filteredSupportPlanCodeList,
      couponType: couponTypes.MARKETING,
      couponSourceType: couponSourceTypeMap.threshold,
      fromPreloadLink: true,
      allowChange,
    };
  }

  /**
   * Get current tier information for progress display
   * @param {Object} thresholdResult - Threshold calculation result
   * @returns {Object} Current tier info
   */
  getCurrentTierInfo(thresholdResult) {
    return {
      currentTierAmount: thresholdResult?.actualTierAmount || 0,
      currentDiscount: thresholdResult?.actualDiscountAmount || 0,
      totalAmount: thresholdResult?.totalAmount || 0,
    };
  }

  /**
   * Get next tier information for progress display
   * @param {Object} thresholdResult - Threshold calculation result
   * @returns {Object} Next tier info
   */
  getNextTierInfo(thresholdResult) {
    return {
      nextTierAmount: thresholdResult?.nextTierAmount || 0,
      nextDiscount: thresholdResult?.nextDiscountAmount || 0,
      gapAmount: thresholdResult?.gapTotalAmount || 0,
      hasNextTier: (thresholdResult?.nextTierAmount || 0) > 0,
    };
  }

  /**
   * Calculate progress percentage towards next tier
   * @param {Object} thresholdResult - Threshold calculation result
   * @returns {number} Progress percentage (0-100)
   */
  getProgressPercentage(thresholdResult) {
    const { totalAmount = 0, nextTierAmount = 0, actualTierAmount = 0 } = thresholdResult || {};

    if (nextTierAmount <= actualTierAmount) {
      return 100; // Already at max tier
    }

    const progress = (totalAmount - actualTierAmount) / (nextTierAmount - actualTierAmount);
    return Math.min(Math.max(progress * 100, 0), 100);
  }

  /**
   * Check if threshold coupon can be applied
   * @param {Object} context - Application context
   * @returns {boolean} Whether coupon can be applied
   */
  // eslint-disable-next-line no-unused-vars
  canApply(context) {
    // Threshold coupons require specific plan codes to be supported
    return true;
  }
}

// Export singleton instance
export const thresholdCouponStrategy = new ThresholdCouponStrategy();
